<template>

    <v-card class="room-evaluation card-standard">
      <v-card-title tag="h2" class="typography-h5 weight-bold d-flex align-center">
        <v-icon icon="mdi-home-analytics" class="mr-2" />
        {{ $t('analysis.roomEval.title') }}
      </v-card-title>

      <v-card-text>
        <div v-if="loading" class="d-flex flex-column align-center ma-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <span class="typography-body text-muted mb-1">{{ $t('analysis.roomEval.loading') }}</span>
          <span class="typography-body wait-message">{{ $t('analysis.roomEval.loadingDetail') }}</span>
        </div>

        <template v-else-if="analysis">
          <v-card class="room-summary-card card-soft mb-6">
            <v-card-item>
              <div class="text-overline text-primary mb-2">
                {{ $t('analysis.roomEval.summary.title') }}
              </div>
              <div class="room-summary-grid">
                <div class="room-summary-item">
                  <span class="room-summary-label">{{ $t('analysis.roomEval.summary.roomCount') }}</span>
                  <strong>{{ roomSummary.roomCount }}</strong>
                </div>
                <div class="room-summary-item">
                  <span class="room-summary-label">{{ $t('analysis.roomEval.summary.averageScore') }}</span>
                  <PremiumContent
                    :is-premium="!hasPremiumAccess"
                    @upgrade="handlePremiumContent(t('analysis.roomEval.summary.title'))"
                  >
                    <strong>{{ roomSummary.averageScore }}</strong>
                  </PremiumContent>
                </div>
                <div class="room-summary-item">
                  <span class="room-summary-label">{{ $t('analysis.roomEval.summary.strongestRoom') }}</span>
                  <PremiumContent
                    :is-premium="!hasPremiumAccess"
                    @upgrade="handlePremiumContent(t('analysis.roomEval.summary.title'))"
                  >
                    <strong>{{ roomSummary.strongestRoom }}</strong>
                  </PremiumContent>
                </div>
                <div class="room-summary-item">
                  <span class="room-summary-label">{{ $t('analysis.roomEval.summary.attentionRoom') }}</span>
                  <PremiumContent
                    :is-premium="!hasPremiumAccess"
                    @upgrade="handlePremiumContent(t('analysis.roomEval.summary.title'))"
                  >
                    <strong>{{ roomSummary.attentionRoom }}</strong>
                  </PremiumContent>
                </div>
              </div>
            </v-card-item>
          </v-card>

          <div
            v-for="(room, index) in analysis.results"
            :key="`${room.raumName}-${index}`"
            class="room-section mb-6"
          >
            <div class="d-flex align-center justify-space-between mb-4">
              <PremiumContent
                :is-premium="isPremiumRoom(index)"
                @upgrade="handlePremiumContent(room.raumName)"
              >
                <span class="typography-h6 weight-semibold">
                  {{ room.raumName }}
                </span>
                <template #fallback>
                  <span class="typography-h6 weight-semibold">
                    {{ $t('analysis.roomEval.room') }}
                  </span>
                </template>
              </PremiumContent>
              <v-rating
                :model-value="room.gesamtbewertung"
                readonly
                density="compact"
                color="primary"
                length="5"
              ></v-rating>
            </div>

            <v-row class="evaluation-grid">
              <!-- Left column with 4 stacked cards -->
              <v-col 
                cols="12" 
                sm="12" 
                md="5" 
                class="pe-md-2"
              >
                <!-- Raumgröße -->
                <v-card class="evaluation-card card-standard mb-4">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-ruler-square" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">
                        {{ $t('analysis.roomEval.size') }}
                      </span>
                    </div>
                    <PremiumContent
                      :is-premium="isPremiumRoom(index)"
                      @upgrade="handlePremiumContent(room.raumName)"
                    >
                      <v-list-item density="compact">
                        <v-list-item-title>
                          <div class="typography-body text-secondary analysis-description text-wrap mb-2">
                            <template v-if="room.groesse.tatsaechlicheGroesse !== -1">
                              {{ room.groesse.tatsaechlicheGroesse }}m² 
                              <span class="text-muted">({{ $t('analysis.roomEval.recommended') }}: {{ room.groesse.empfohleneGroesse }}m²)</span>
                            </template>
                            <template v-else>
                              <v-alert
                                type="info"
                                density="compact"
                                variant="text"
                                class="pa-0 text-wrap"
                              >
                                {{ $t('analysis.roomEval.sizeNotDetermined') }}
                                <span class="text-muted text-wrap">
                                  ({{ $t('analysis.roomEval.recommended') }}: {{ room.groesse.empfohleneGroesse }}m²)
                                </span>
                              </v-alert>
                            </template>
                          </div>
                          <v-rating
                            v-if="room.groesse.tatsaechlicheGroesse !== -1"
                            :model-value="calculateSizeScore(room.groesse.tatsaechlicheGroesse, room.groesse.empfohleneGroesse)"
                            readonly 
                            density="compact"
                            color="primary"
                            length="5"
                            class="mt-2 mb-3"
                          ></v-rating>
                        </v-list-item-title>
                      </v-list-item>
                    </PremiumContent>
                  </v-card-item>
                </v-card>

                <!-- Möblierung -->
                <v-card class="evaluation-card card-standard mb-4">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-sofa" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.furniture') }}</span>
                    </div>
                    <v-list-item density="compact">
                      <div class="d-flex align-center">
                        <v-rating
                          :model-value="room.moebel_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div>
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.moebel_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>

                <!-- Fenster und Belichtung -->
                <v-card class="evaluation-card card-standard mb-4">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-window-open" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.windows') }}</span>
                    </div>
                    <v-list-item density="compact">
                      <div class="d-flex align-center">
                        <v-rating
                          :model-value="room.fenster_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div>
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.fenster_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>

                <!-- Energieeffizienz -->
                <v-card class="evaluation-card card-standard">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-lightning-bolt" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.energy') }}</span>
                    </div>
                    <v-list-item density="compact">
                      <div class="d-flex align-center">
                        <v-rating
                          :model-value="room.energieeffizienz_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div>
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.energieeffizienz_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>
              </v-col>

              <!-- Right column with room aspects -->
              <v-col 
                cols="12" 
                sm="12" 
                md="7" 
                class="ps-md-2"
              >
                <!-- Stauraum -->
                <v-card class="evaluation-card card-standard mb-4">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-storage-tank-outline" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.storage') }}</span>
                    </div>
                    <v-list-item class="mb-4">
                      <div class="d-flex align-center mb-3">
                        <v-rating
                          :model-value="room.stauraum_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div> 
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.stauraum_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>

                <!-- Flexibilität -->
                <v-card class="evaluation-card card-standard mb-4">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-sync" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.flexibility') }}</span>
                    </div>
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-rating
                          :model-value="room.flexibilitaet_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div>
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.flexibilitaet_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>

                <!-- Barrierefreiheit -->
                <v-card class="evaluation-card card-standard">
                  <v-card-item>
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-wheelchair" class="mr-2" size="24" color="primary"/>
                      <span class="typography-h6 weight-semibold">{{ $t('analysis.roomEval.barrierFree') }}</span>
                    </div>
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-rating
                          :model-value="room.barrierefrei_bewertung.score"
                          readonly
                          density="compact"
                          color="primary"
                          length="5"
                        ></v-rating>
                      </div>
                      <PremiumContent 
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item-subtitle class="typography-body text-secondary analysis-description text-wrap mt-2">
                          <span v-html="formatMarkdownLite(room.barrierefrei_beschreibung)"></span>
                        </v-list-item-subtitle>
                      </PremiumContent>
                      
                      <PremiumContent
                        :is-premium="isPremiumRoom(index)"
                        @upgrade="handlePremiumContent(room.raumName)"
                      >
                        <v-list-item v-if="room.tuerbreite || room.bewegungsflaeche">
                          <v-list>
                            <v-list-item>
                              <v-icon icon="mdi-door" class="mr-2" />
                              {{ room.tuerbreite }} m
                            </v-list-item>
                            <v-list-item>
                              <v-icon icon="mdi-rotate-orbit" class="mr-2" />
                              {{ room.bewegungsflaeche }} m
                            </v-list-item>
                            <v-list-item v-if="room.schwellen">
                              <v-icon icon="mdi-stairs" class="mr-2" />
                              {{ $t('analysis.roomEval.threshold') }}: {{ translateThreshold(room.schwellen) }}
                            </v-list-item>
                          </v-list>
                        </v-list-item>
                      </PremiumContent>
                    </v-list-item>
                  </v-card-item>
                </v-card>
              </v-col>
            </v-row>

            <v-divider v-if="index !== analysis.results.length - 1" class="my-6"></v-divider>
          </div>
        </template>

        <v-alert
          v-else-if="hasError"
          type="error"
          class="mt-2"
        >
          {{ $t('analysis.roomEval.error') }}
          <br />
          <v-btn
            color="primary"
            variant="text"
            class="mt-2"
            :loading="loading"
            @click="loadAnalysis"
          >
            <v-icon icon="mdi-refresh" class="mr-2" />
            {{ $t('common.retry') }}
          </v-btn>
        </v-alert>
      </v-card-text>
    </v-card>

</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import PremiumContent from '@/components/common/PremiumContent.vue'
import { trackRoomPremiumClick, trackUnlockCtaClicked } from '@/utils/analytics'
import { useStore } from 'vuex'
import { getIdToken } from 'firebase/auth'
import { formatMarkdownLite } from '@/utils/textFormatting'
import { scrollToUnlockPanel } from '@/utils/scrollToUnlockPanel'
import { buildUnlockCtaPayload } from '@/utils/unlockTracking.mjs'

const props = defineProps({
  imageUrl: {
    type: String,
    required: true
  }
})

// Convert analysis from ref to computed
const analysis = computed(() => store.state.analysisRooms || null)
const { locale, t } = useI18n()
const authStore = useAuthStore()
const hasPremiumAccess = computed(() => store.state.userHadPremiumWhenUploaded)
const store = useStore()
const sectionStatus = computed(() => store.state.analysisSectionStatus?.rooms || 'idle')
const loading = computed(() => sectionStatus.value === 'loading')
const hasError = computed(() => sectionStatus.value === 'error')

// Update isPremium logic to use credits
const isPremiumRoom = (index) => {
  if (hasPremiumAccess.value) return false
  return index > 0
}

const calculateSizeScore = (actual, recommended) => {
  if (actual === -1 || recommended === 0) return 0;
  const ratio = actual / recommended;
  if (ratio >= 0.9 && ratio <= 1.2) return 5;
  if (ratio >= 0.8 && ratio < 0.9 || ratio > 1.2 && ratio <= 1.4) return 4;
  if (ratio >= 0.7 && ratio < 0.8 || ratio > 1.4 && ratio <= 1.6) return 3;
  if (ratio >= 0.5 && ratio < 0.7 || ratio > 1.6 && ratio <= 1.8) return 2;
  return 1;
};

// Update premium content handler
const handlePremiumContent = (roomName) => {
  trackRoomPremiumClick(roomName)
  trackUnlockCtaClicked(buildUnlockCtaPayload({
    source: 'room_evaluation',
    userIntent: store.state.userIntent,
    extra: {
      roomName,
    },
  }))
  
  if (!hasPremiumAccess.value) {
    navigateToPricing()
  }
}

// Add navigation helper
const navigateToPricing = () => {
  scrollToUnlockPanel()
}

// Modify the loadAnalysis function
const loadAnalysis = async () => {
  if (loading.value) {
    return
  }

  if (analysis.value && store.state.lastAnalyzedImage === props.imageUrl) {
    return
  }
  
  try {
    const authHeader = authStore.isAuthenticated
      ? `Bearer ${await getIdToken(authStore.user)}`
      : undefined

    await store.dispatch('preloadRoomAnalysis', {
      imageUrl: props.imageUrl,
      language: locale.value,
      authHeader,
    })
  } catch (error) {
    console.error('Failed to load room analysis:', error)
  }
}

// Update onMounted to check for preloaded data
onMounted(() => {
  if (props.imageUrl && !analysis.value && sectionStatus.value === 'idle') {
    loadAnalysis()
  }
})

const translateThreshold = (threshold) => {
  return threshold ? t(`analysis.roomEval.thresholds.${threshold}`) : '';
};

const roomSummary = computed(() => {
  const rooms = analysis.value?.results || []
  if (!rooms.length) {
    return {
      roomCount: 0,
      averageScore: '0.0 / 5',
      strongestRoom: '-',
      attentionRoom: '-',
    }
  }

  const sortedRooms = [...rooms].sort((left, right) => right.gesamtbewertung - left.gesamtbewertung)
  const averageScore = (
    rooms.reduce((sum, room) => sum + Number(room.gesamtbewertung || 0), 0) / rooms.length
  ).toFixed(1)

  return {
    roomCount: rooms.length,
    averageScore: `${averageScore} / 5`,
    strongestRoom: sortedRooms[0]?.raumName || '-',
    attentionRoom: sortedRooms[sortedRooms.length - 1]?.raumName || '-',
  }
})
</script>

<style scoped>
.room-evaluation {
  border-radius: 8px;
}

.room-summary-card {
}

.room-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.room-summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
}

.room-summary-label {
  font-size: var(--font-size-caption);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Phones: the uppercase + letter-spacing inflates labels like
   "Braucht Aufmerksamkeit" enough to wrap into a stranded "T" on the
   second line. Use natural case on xs — the labels stay readable and
   fit cleanly in the 2-column grid. */
@media (max-width: 600px) {
  .room-summary-label {
    text-transform: none;
    letter-spacing: 0;
    font-weight: var(--font-weight-medium);
  }
}

.room-summary-item :deep(.description-container) {
  display: block;
  min-height: 32px;
}

.evaluation-grid {
  gap: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
}

/* Remove padding from columns by default */
:deep(.v-col) {
  padding: 0;
}

/* Add specific padding for the columns when side by side */
@media (min-width: 960px) {
  .pe-md-2 {
    padding-right: 8px;
  }

  .ps-md-2 {
    padding-left: 8px;
  }
}

/* Ensure cards have proper spacing */
.evaluation-card {
  height: auto;
  margin-bottom: 16px;
}

/* Remove margin from last card */
.evaluation-card:last-child {
  margin-bottom: 0;
}

/* Ensure right column card fills height */
.h-100 {
  height: 100%;
}

/* Add margin between cards only on mobile */
@media (max-width: 959px) {
  .evaluation-card.mb-4 {
    margin-bottom: 8px;
  }
}

/* Adjust card item padding based on screen size */
:deep(.v-card-item) {
  padding: 4px;
}

@media (min-width: 600px) {
  :deep(.v-card-item) {
    padding: 16px;
  }
}

/* Adjust room section padding */
.room-section {
  padding: 4px;
  border-radius: 8px;
  background-color: var(--v-background);
}

@media (min-width: 600px) {
  .room-section {
    padding: 8px;
  }
}

/* Remove padding from v-card-text on mobile */
:deep(.v-card-text) {
  padding: 4px;
}

/* Remove padding from list items on mobile */
:deep(.v-list-item) {
  padding: 6px;
}

@media (min-width: 600px) {
  :deep(.v-list-item) {
    padding: 0 8px;
  }
}

/* Adjust v-col padding on mobile */
:deep(.v-col) {
  padding: 2px !important;
}

@media (min-width: 600px) {
  :deep(.v-col) {
    padding: 12px !important;
  }
}

/* Adjust spacing between elements */
:deep(.mb-4) {
  margin-bottom: 6px !important;
}

@media (min-width: 600px) {
  :deep(.mb-4) {
    margin-bottom: 16px !important;
  }
}

:deep(.mb-3) {
  margin-bottom: 4px !important;
}

@media (min-width: 600px) {
  :deep(.mb-3) {
    margin-bottom: 12px !important;
  }
}

:deep(.mb-2) {
  margin-bottom: 3px !important;
}

@media (min-width: 600px) {
  :deep(.mb-2) {
    margin-bottom: 8px !important;
  }
}

/* Adjust icon margins */
.mr-2 {
  margin-right: 3px !important;
}

@media (min-width: 600px) {
  .mr-2 {
    margin-right: 8px !important;
  }
}

/* Remove old premium content styles */
.premium-content {
  user-select: none;
  filter: blur(4px);
}

/* Add new consistent blur style */
.premium-blur {
  filter: blur(4px);
  user-select: none;
  cursor: pointer;
  display: inline-block;
  transition: filter 0.2s ease;
}

.premium-blur:hover {
  filter: blur(6px);
}

.premium-message {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
  text-align: center;
  padding: 4px 12px;
  font-size: var(--font-size-body-2);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  pointer-events: auto;
  z-index: 1;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.premium-message-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.premium-item {
  position: relative;
}

.premium-item:hover .premium-message {
  display: flex;
}

/* Add styles for formatted text */
:deep(strong) {
  font-weight: var(--font-weight-semibold);
}

:deep(em) {
  font-style: italic;
  color: var(--v-primary-base);
}

/* Typography and text styles */
.analysis-description {
  white-space: normal !important;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
}

.wait-message {
  color: var(--v-primary-base);
}

/* Remove custom typography styles and let Vuetify handle it */
:deep(.v-card-title) {
  font-size: inherit;
  font-weight: inherit;
}

:deep(h1), :deep(h2), :deep(h3) {
  margin: 0.5em 0;
}

:deep(ul), :deep(ol) {
  padding-left: 1.5em;
  margin: 0.5em 0;
}
</style>
